// src/components/LfgLocationsTab.jsx
// Admin tab for managing LFG-owned funeral home locations

import React, { useState, useRef, useMemo } from 'react';
import { Plus, X, Loader2, MapPin, Edit2, Trash2, Search, Check, Upload, Download, Building2, Flame } from 'lucide-react';
import { useLfgLocations } from '../hooks/useSupabase';

export default function LfgLocationsTab({ organizationId, primaryColor, secondaryColor, showToast }) {
  const { 
    locations, 
    loading, 
    addLocation, 
    updateLocation, 
    deleteLocation,
    geocodeAddress,
  } = useLfgLocations(organizationId);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [groupBy, setGroupBy] = useState('source'); // 'source', 'state', 'none'
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    latitude: '',
    longitude: '',
    is_crematory: false,
    is_current_lfg: false,
    notes: ''
  });

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      latitude: '',
      longitude: '',
      is_crematory: false,
      is_current_lfg: false,
      notes: ''
    });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleGeocode = async () => {
    if (!formData.address || !formData.city || !formData.state) {
      showToast('Please enter address, city, and state first', 'error');
      return;
    }

    setGeocoding(true);
    try {
      const result = await geocodeAddress(formData.address, formData.city, formData.state, formData.zip);
      if (result) {
        setFormData(prev => ({
          ...prev,
          latitude: result.latitude.toFixed(6),
          longitude: result.longitude.toFixed(6)
        }));
        showToast('Location found!');
      } else {
        showToast('Could not find coordinates for this address', 'error');
      }
    } catch (err) {
      showToast('Geocoding failed', 'error');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showToast('Please enter a location name', 'error');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: formData.name.trim(),
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state.trim().toUpperCase() || null,
        zip: formData.zip.trim() || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        is_crematory: formData.is_crematory || false,
        is_current_lfg: formData.is_current_lfg || false,
        notes: formData.notes.trim() || null
      };

      if (editingId) {
        await updateLocation(editingId, data);
        showToast('Location updated');
      } else {
        await addLocation(data);
        showToast('Location added');
      }
      resetForm();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (location) => {
    setFormData({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zip: location.zip || '',
      latitude: location.latitude?.toString() || '',
      longitude: location.longitude?.toString() || '',
      is_crematory: location.is_crematory || false,
      is_current_lfg: location.is_current_lfg || false,
      notes: location.notes || ''
    });
    setEditingId(location.id);
    setShowAddForm(true);
  };

  const handleDelete = async (location) => {
    if (!confirm(`Remove "${location.name}" from LFG locations?`)) return;
    
    try {
      await deleteLocation(location.id);
      showToast('Location removed');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // CSV Import functionality
  const downloadTemplate = () => {
    const template = 'name,address,city,state,zip,latitude,longitude,is_current_lfg,notes\n"Example Funeral Home","123 Main St","Columbus","OH","43215","39.9612","-82.9988","true","Optional notes"';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lfg-locations-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const header = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    
    const results = [];
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      const row = {};
      header.forEach((h, idx) => {
        row[h] = values[idx]?.replace(/"/g, '') || '';
      });
      
      if (row.name) {
        results.push(row);
      }
    }
    
    return results;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportProgress({ total: 0, current: 0, success: 0, failed: 0, skipped: 0 });
    
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        showToast('No valid rows found in CSV', 'error');
        setImporting(false);
        return;
      }
      
      setImportProgress({ total: rows.length, current: 0, success: 0, failed: 0, skipped: 0 });
      
      let success = 0;
      let failed = 0;
      let skipped = 0;
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        try {
          // Check for duplicates
          const exists = locations.some(loc => 
            loc.name === row.name && 
            (loc.address || '') === (row.address || '') &&
            (loc.city || '') === (row.city || '')
          );
          
          if (exists) {
            skipped++;
            setImportProgress({ total: rows.length, current: i + 1, success, failed, skipped });
            continue;
          }
          
          let lat = row.latitude ? parseFloat(row.latitude) : null;
          let lng = row.longitude ? parseFloat(row.longitude) : null;
          
          // If no coordinates, try to geocode
          if ((!lat || !lng) && row.address && row.city && row.state) {
            const coords = await geocodeAddress(row.address, row.city, row.state, row.zip);
            if (coords) {
              lat = coords.latitude;
              lng = coords.longitude;
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          await addLocation({
            name: row.name,
            address: row.address || null,
            city: row.city || null,
            state: row.state?.toUpperCase() || null,
            zip: row.zip || null,
            latitude: lat,
            longitude: lng,
            is_current_lfg: row.is_current_lfg === 'true' || row.is_current_lfg === '1' || row.is_current_lfg === 'yes' || false,
            notes: row.notes || null
          });
          
          success++;
        } catch (err) {
          console.error('Failed to import row:', row, err);
          failed++;
        }
        
        setImportProgress({ total: rows.length, current: i + 1, success, failed, skipped });
      }
      
      let msg = `Imported ${success} locations`;
      if (skipped > 0) msg += `, ${skipped} skipped (duplicates)`;
      if (failed > 0) msg += `, ${failed} failed`;
      showToast(msg);
    } catch (err) {
      showToast('Failed to parse CSV file', 'error');
    } finally {
      setImporting(false);
      setImportProgress(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Extract source from notes (e.g., "From: Walker Funeral Homes")
  const getSource = (location) => {
    if (!location.notes) return 'Manual Entry';
    const match = location.notes.match(/^From:\s*(.+?)(?:\s*\(|$)/);
    return match ? match[1].trim() : 'Manual Entry';
  };

  // Group locations
  const groupedLocations = useMemo(() => {
    if (groupBy === 'none') {
      return { 'All Locations': [...locations].sort((a, b) => a.name.localeCompare(b.name)) };
    }
    
    if (groupBy === 'state') {
      return locations.reduce((acc, loc) => {
        const state = loc.state || 'Unknown';
        if (!acc[state]) acc[state] = [];
        acc[state].push(loc);
        return acc;
      }, {});
    }
    
    // Group by source (from notes)
    return locations.reduce((acc, loc) => {
      const source = getSource(loc);
      if (!acc[source]) acc[source] = [];
      acc[source].push(loc);
      return acc;
    }, {});
  }, [locations, groupBy]);

  const sortedGroups = Object.keys(groupedLocations).sort((a, b) => {
    // Put "Manual Entry" last
    if (a === 'Manual Entry') return 1;
    if (b === 'Manual Entry') return -1;
    return a.localeCompare(b);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: primaryColor }}>LFG Locations</h2>
          <p className="text-sm" style={{ color: secondaryColor }}>
            Existing LFG-owned funeral homes. These appear on the map.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
            className="px-3 py-2 border rounded text-sm"
          >
            <option value="source">Group by Source</option>
            <option value="state">Group by State</option>
            <option value="none">No Grouping</option>
          </select>
          <button
            onClick={downloadTemplate}
            className="px-3 py-2 border rounded flex items-center gap-2 text-sm hover:bg-gray-50"
          >
            <Download className="w-4 h-4" /> Template
          </button>
          <label className="px-3 py-2 border rounded flex items-center gap-2 text-sm hover:bg-gray-50 cursor-pointer">
            <Upload className="w-4 h-4" /> Import CSV
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              disabled={importing}
            />
          </label>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-3 py-2 text-white rounded flex items-center gap-2 text-sm hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            <Plus className="w-4 h-4" /> Add Location
          </button>
        </div>
      </div>

      {/* Import Progress */}
      {importing && importProgress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <span className="font-medium text-blue-800">
              Importing... {importProgress.current} of {importProgress.total}
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
            />
          </div>
          <div className="text-sm text-blue-700 mt-2">
            {'\u2713'} {importProgress.success} added
            {importProgress.skipped > 0 && <span className="text-yellow-600"> {'\u2022'} {importProgress.skipped} skipped</span>}
            {importProgress.failed > 0 && <span className="text-red-600"> {'\u2022'} {importProgress.failed} failed</span>}
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium" style={{ color: primaryColor }}>
              {editingId ? 'Edit Location' : 'Add New Location'}
            </h3>
            <button onClick={resetForm} className="p-1 hover:bg-gray-200 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm mb-1 font-medium">Location Name *</label>
              <input
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Smith Family Funeral Home"
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm mb-1 font-medium">Street Address</label>
              <input
                value={formData.address}
                onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
                placeholder="123 Main Street"
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm mb-1 font-medium">City</label>
              <input
                value={formData.city}
                onChange={e => setFormData(p => ({ ...p, city: e.target.value }))}
                placeholder="City"
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm mb-1 font-medium">State</label>
                <input
                  value={formData.state}
                  onChange={e => setFormData(p => ({ ...p, state: e.target.value.toUpperCase().slice(0, 2) }))}
                  placeholder="OH"
                  maxLength={2}
                  className="w-full border rounded px-3 py-2 text-sm uppercase"
                />
              </div>
              <div>
                <label className="block text-sm mb-1 font-medium">ZIP</label>
                <input
                  value={formData.zip}
                  onChange={e => setFormData(p => ({ ...p, zip: e.target.value }))}
                  placeholder="43215"
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="col-span-2 flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm mb-1 font-medium">Latitude</label>
                <input
                  value={formData.latitude}
                  onChange={e => setFormData(p => ({ ...p, latitude: e.target.value }))}
                  placeholder="39.9612"
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm mb-1 font-medium">Longitude</label>
                <input
                  value={formData.longitude}
                  onChange={e => setFormData(p => ({ ...p, longitude: e.target.value }))}
                  placeholder="-82.9988"
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <button
                onClick={handleGeocode}
                disabled={geocoding || (!formData.address && !formData.city)}
                className="px-3 py-2 border rounded text-sm flex items-center gap-1 hover:bg-gray-100 disabled:opacity-50"
              >
                {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Lookup
              </button>
            </div>

            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.is_crematory} 
                  onChange={e => setFormData(p => ({ ...p, is_crematory: e.target.checked }))} 
                  className="rounded" 
                />
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-sm">Crematory</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.is_current_lfg} 
                  onChange={e => setFormData(p => ({ ...p, is_current_lfg: e.target.checked }))} 
                  className="rounded" 
                />
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="text-sm">Current LFG Property</span>
              </label>
            </div>

            <div className="col-span-2">
              <label className="block text-sm mb-1 font-medium">Notes</label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Any additional notes"
                rows={2}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={resetForm} className="px-4 py-2 border rounded hover:bg-gray-50 text-sm">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim()}
              className="px-4 py-2 text-white rounded text-sm flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editingId ? 'Update' : 'Add'} Location
            </button>
          </div>
        </div>
      )}

      {locations.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <MapPin className="w-12 h-12 mx-auto mb-3" style={{ color: secondaryColor }} />
          <p style={{ color: secondaryColor }}>No LFG locations added yet</p>
          <p className="text-sm mt-1" style={{ color: secondaryColor }}>
            Add locations manually or import from CSV
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedGroups.map(group => (
            <div key={group}>
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: secondaryColor }}>
                <Building2 className="w-4 h-4" />
                {group} ({groupedLocations[group].length})
              </h3>
              <div className="bg-white rounded-lg border overflow-hidden">
                {groupedLocations[group]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((location, idx) => (
                  <div 
                    key={location.id}
                    className={`p-3 flex items-center justify-between hover:bg-gray-50 ${idx > 0 ? 'border-t' : ''}`}
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm flex items-center gap-1.5">
                        {(location.is_crematory || (location.notes && location.notes.includes('(Crematory)'))) && <Flame className="w-3.5 h-3.5 text-orange-500" title="Crematory" />}
                        {location.is_current_lfg && <Building2 className="w-3.5 h-3.5 text-blue-600" title="Current LFG Property" />}
                        {location.name}
                      </div>
                      {location.address && (
                        <div className="text-xs mt-0.5" style={{ color: secondaryColor }}>
                          {location.address}, {location.city}, {location.state} {location.zip}
                        </div>
                      )}
                      {!location.latitude && !location.longitude && (
                        <div className="text-xs text-orange-600 mt-0.5">
                          {'26A0'} No coordinates - won't appear on map
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(location)}
                        className="p-1.5 rounded hover:bg-gray-100"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" style={{ color: secondaryColor }} />
                      </button>
                      <button
                        onClick={() => handleDelete(location)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {locations.length > 0 && (
        <div className="text-sm" style={{ color: secondaryColor }}>
          Total: {locations.length} location{locations.length !== 1 ? 's' : ''} across {sortedGroups.length} {groupBy === 'state' ? 'state' : 'source'}{sortedGroups.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
